// Frontend application configuration (ES module, bundled by Vite).
// Server-side config lives in ./index.js (CommonJS).
export const CONFIG = {
  // Agora Configuration
  // Note: neither the app ID nor the channel is stored here. The app ID comes
  // back on the /api/agora/token response (single source: src/routes/agora.js),
  // and the CHANNEL IS THE ROOM NAME - see agoraChannel() in src/videochat.js.
  // There is deliberately no shared constant channel: the token route requires
  // the session token's roomName to equal the channel it is asked to sign, and
  // one channel for every room would put unrelated sessions in one call.

  // Photon Configuration
  PHOTON: {
    APP_ID: '4ce1ed5f-476e-4b9d-8dfd-8b56ee766fa4',
  },

  // Arrival screen session metadata (the unified entry screen in index.html:
  // name + avatar color + optional device check). `enabled: false` hides the
  // camera/mic section; entering never requires it either way. meetingTitle
  // also feeds the in-world welcome toast.
  ARRIVAL: {
    enabled: true,
    meetingTitle: 'Oggi Demo', // shown as a session chip on the arrival hero
    privacyNote:
      'Your camera and microphone may be shared with other participants in this room. You can turn them off at any time.',
  },

  // Server Configuration
  SERVER: {
    PORT: 5001,
    API_BASE: '/api',
  },

  // UI Configuration
  UI: {
    FPS_UPDATE_INTERVAL: 100, // ms
    TOKEN_REFRESH_BUFFER: 300, // seconds before expiry
    OVERLAY_Z_INDEX: 50,
    OVERLAY_POSITION: {
      BOTTOM: '16px',
      RIGHT: '16px',
    },
    MOBILE_BUTTON: {
      WIDTH: '56px',
      HEIGHT: '56px',
      FONT_SIZE: '1.5rem',
      BORDER_RADIUS: '50%',
      BACKGROUND: 'rgba(25,118,210,0.9)',
      COLOR: '#fff',
      BOX_SHADOW: '0 2px 8px rgba(0,0,0,0.2)',
    },
    MINIMAP: {
      WIDTH: 150,
      HEIGHT: 100,
      BORDER_RADIUS: '8px',
      BORDER: '2px solid #fff',
      BOX_SHADOW: '0 2px 8px rgba(0,0,0,0.2)',
    },
  },

  // Live role promotion (a host flips a participant guest <-> presenter
  // mid-session over Photon event 51). Wire protocol:
  // src/utils/rolePromotionProtocol.ts; see docs/live-role-promotion.md.
  ROLE: {
    EVENT_CODE: 51,
    // The grant is a server-minted JWT; anything longer is malformed, and the
    // cap keeps a hostile payload from reaching the API layer at all.
    MAX_GRANT_LENGTH: 1024,
  },

  // Text chat (session / group / DM over Photon event 55). Messages are
  // ephemeral — client memory only, no persistence, no late-joiner history.
  CHAT: {
    EVENT_CODE: 55,
    MAX_MESSAGE_LENGTH: 500,
    MAX_MESSAGES_PER_CONVERSATION: 200,
    MAX_GROUP_SIZE: 16,
    MAX_GROUP_NAME_LENGTH: 40,
  },

  // Breakout rooms (host-orchestrated small groups in per-group lounge
  // levels, Photon event 57 + the `breakoutState` room custom property).
  // Wire protocol: src/utils/breakoutProtocol.ts; see docs/breakout-rooms.md.
  BREAKOUT: {
    EVENT_CODE: 57,
    MAX_ROOMS: 8,
    MAX_NAME_LENGTH: 40,
    GRACE_MS: 30000, // close countdown before everyone auto-returns to main
    PRE_MOVE_MS: 5000, // assigned-guest warning banner before the move
    ROOM_PROP: 'breakoutState',
  },

  // Walk-in polls (a host poses a 2-4 option question; participants vote by
  // standing in lettered floor zones; counts derive locally from the 10Hz
  // position stream - only the poll definition rides Photon event 58 + the
  // `pollState` room custom property). Wire protocol:
  // src/utils/pollProtocol.ts; see docs/walk-in-polls.md.
  POLL: {
    EVENT_CODE: 58,
    ROOM_PROP: 'pollState',
    MAX_QUESTION_LENGTH: 140,
    MAX_OPTION_LABEL_LENGTH: 40,
    MIN_OPTIONS: 2,
    MAX_OPTIONS: 4,
    // Hysteresis: a voter stays in their zone until they walk past
    // radius * (1 + EXIT_MARGIN), so pacing the boundary never flickers.
    EXIT_MARGIN: 0.15,
  },

  // Raise hand + speaking queue. Each actor's hand lives in that actor's OWN
  // `handRaisedAt` custom property, so there is no shared mutable state to
  // race on and a hand dies with its actor. The queue is derived from the
  // roster, never stored. Photon event 68 is the live nudge plus the carrier
  // for host actions; the `qaState` room property holds only who has the
  // floor. Wire protocol: src/utils/handQueueProtocol.ts; see
  // docs/raise-hand.md.
  QA: {
    EVENT_CODE: 68,
    ROOM_PROP: 'qaState',
    /** Per-actor property holding the raise timestamp (epoch ms). */
    HAND_PROP: 'handRaisedAt',
    // Expiry is self-enforced (the basketball hold posture): the raiser lowers
    // its own hand after MAX_HAND_MS, and receivers drop anything older than
    // the more generous HAND_STALE_MS so a crashed tab cannot hold the queue.
    // Two thresholds instead of one because Date.now() is per-client - the gap
    // absorbs clock skew rather than letting it cause disagreement.
    MAX_HAND_MS: 600000,
    HAND_STALE_MS: 720000,
    /** Defensive cap on a derived queue; far above any real room. */
    MAX_QUEUE: 64,
  },

  // Shared "nearby" proximity model: chat picker, participants panel, and
  // proximity audio all use the same radii so "in the Nearby list" always
  // means "within voice range". Distances in world units. Enter the Nearby
  // list at <= RADIUS, leave at > EXIT_RADIUS - the dead band keeps a player
  // pacing the boundary from flickering in and out of the list.
  // (For scale: presenter follow distance is 5, webcam mid tier is 30.)
  NEARBY: {
    RADIUS: 20, // enter Nearby list / list membership
    EXIT_RADIUS: 25, // leave list; also where audio reaches silence
    REFRESH_MS: 1000, // UI re-poll interval
    AUDIO_FULL_RADIUS: 8, // full volume within this distance, linear fade to 0 at EXIT_RADIUS
  },

  // Huddle circles: stand close to a few people and your voice goes
  // semi-private to the group (outsiders hear you muffled, never silent), with
  // a soft dome around you. Like polls, membership is DERIVED locally from the
  // existing 10Hz position stream on every client - there is no wire protocol
  // and no authority; the same deterministic clustering runs everywhere. Pure
  // math: src/utils/huddleTracker.ts; see docs/huddle-circles.md. Distances in
  // world units. (For scale: presenter follow distance is 5, full-volume audio
  // radius is 8, poll zone discs are radius ~2.)
  HUDDLE: {
    // A pair must stay within JOIN_RADIUS continuously for DWELL_MS before it
    // forms/joins a huddle; a member stays until they leave EXIT_RADIUS of the
    // huddle centroid. The JOIN -> EXIT gap is the leave hysteresis, so pacing
    // the edge never flickers the dome.
    JOIN_RADIUS: 3,
    EXIT_RADIUS: 4.5,
    // Sticky pair timer: keep the dwell clock running while within
    // JOIN_RADIUS * (1 + PAIR_EXIT_MARGIN) (the poll EXIT_MARGIN idiom).
    PAIR_EXIT_MARGIN: 0.15,
    // Vertical gate: never huddle across floors (local camera eye height vs a
    // remote pod root differ by ~1-2u, so this is deliberately generous).
    Y_TOLERANCE: 3,
    // Sustained-proximity delay before a pair counts - the formation feel.
    DWELL_MS: 2000,
    // Gain multiplier applied to voices across a huddle boundary (outsider <->
    // huddler, and between two different huddles). Muffled, never silent, so a
    // momentary cross-client membership disagreement can't hard-cut anyone.
    MUFFLE: 0.12,
    // Soft dome visual. Radius fits the members plus MARGIN, clamped; ALPHA is
    // the resting translucency; the breathing pulse cycles over PULSE_MS.
    DOME: { MIN_RADIUS: 2.2, MAX_RADIUS: 6, MARGIN: 1.1, ALPHA: 0.18, PULSE_MS: 2800 },
  },

  // Game Configuration
  GAME: {
    DEFAULT_ROOM: 'default',
    MAX_ROOM_NAME_LENGTH: 24,
    AVATAR_COLORS: [
      { r: 1, g: 0, b: 0 }, // Red
      { r: 0, g: 1, b: 0 }, // Green
      { r: 0, g: 0, b: 1 }, // Blue
      { r: 1, g: 1, b: 0 }, // Yellow
      { r: 1, g: 0, b: 1 }, // Magenta
      { r: 0, g: 1, b: 1 }, // Cyan
    ],
    WALL_HEIGHT: 10,
    WALL_THICKNESS: 1,
    GROUND_SIZE: 50,
    CAMERA_HEIGHT: 2,
    RESPAWN_HEIGHT: -50,
    RESPAWN_POSITION: { x: 0, y: 2, z: -10 },
  },

  // Texture Configuration
  TEXTURES: {
    GRASS: {
      BASE_SCALE: 4.0,
      LARGE_FIELD_SCALE: 40.0,
    },
    BRICK: {
      U_SCALE: 0.5,
      V_SCALE: 10.0,
      ROTATION: Math.PI / 2,
    },
    EARTH: {
      BASE_SCALE: 4.0,
    },
  },
};
